// tslint:disable:no-console
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { ErrorMessage, INavbarLink, INavigationItemProps, PageLoading, SaveDialog, usePrevious } from 'ui-toolkit';
import 'ui-toolkit/scss/00_styleguide/_app.scss';
import generateAddRowActions from './utilities/generateAddRowActions';
import orderStatusIsNotLateStageOrArchived from './utilities/orderStatusIsNotLateStageOrArchived';
import '../../scss/_cd-builder.scss';
import '../../scss/_cd-title.scss';
import { generateClosingDisclosureAsync } from '../../services/RestDocumentsService';
import CdNavigation from './CdNavigation';
import { generateSectionItems } from './generateSectionItems';
import { generateServicesSectionItems } from './generateServicesSectionItems';
import generateTaxesSectionItems from './generateTaxesSectionItems';
import LoanCosts from './LoanCosts';
import LoanDetails from './LoanDetails';
import { NavigateAwayHelper } from './NavigateAwayHelper';
import NotificationLenderPremiumConditionalRate from './NotificationLenderPremiumConditionalRate';
import OtherCosts from './OtherCosts/OtherCosts';
import PayoffsAndPayments from './PayoffsAndPayments';
import PurchaseCosts from './PurchaseCosts';
import RefiDetailsModal from './RefiDetailsModal';
import RefiEligibilityModal from './RefiEligibilityModal';
import TotalClosingCosts from './TotalClosingCosts/TotalClosingCosts';
import { ValidationSummary } from './Validation';
import { ModalHandler } from './Modals/ModalHandler';
import { updateMoveAttributesBasedOnType } from './utilities/updateMoveAttributesBasedOnType';
import { useSelector } from 'react-redux';
import {
  loadItemsData,
  removeItem,
  replaceCdItem,
  setRefiDiscountByState,
  validateAndSave,
} from '../../store/order/cdBuilder';
import { RootStore } from '../../store/types';
import { useApplicationContext } from './context/useApplicationContext';
import { ePermissions } from '../../types/administration/userAdministration/ePermissions';
import { BlockRedirection } from './BlockRedirection';
import { orderNavigationSlice } from 'store/order/orderNavigation/orderNavigationSlice';
import { getOwnerDetails } from './getOwnerDetails';
import { ReissueDiscountAlertContainer } from 'components/order/discounts/ReissueDiscountAlertContainer';
import { useAppDispatch } from 'store/store';
import { ReissueDiscountsContainer } from 'components/order/discounts/ReissueDiscountsContainer';
import { cdBuilderNavigationOptions } from './cdBuilderNavigationOptions';
import { useHasPermissions } from '../accessControl/hooks/useHasPermissions';
import { useUpdateOrderTimberHook } from '../../API/order/useUpdateOrderHook';
import { ICDV3ViewItem } from 'ui-toolkit/cdItems/interfaces/ICDViewItem';
import { IOwnerDetailsServiceItem } from '../../ui-toolkit/cdItems/service/ServiceInterfaces';
import { eTitleFees } from '../../ui-toolkit/enums/eTitleFees';
import { CDItemTypesBySection } from 'ui-toolkit/enums/CDItemTypesBySection';
import { eCDV3ItemTypes } from '../../ui-toolkit/enums/eCDV3ItemTypes';
import { ePartyPurpose } from '../../ui-toolkit/enums/ePartyPurpose';
import { IRefiDiscount } from '../../ui-toolkit/orders/interfaces/IRefiDiscount';
import { FullName } from '../../ui-toolkit/orders/interfaces/IParty';
import { IOrderDetailsData } from '../../ui-toolkit/orders/interfaces/IOrderDetailsData';

export type CdBuilderSection = 'section-a-j' | 'section-k-n';

export interface ICdBuilderProps {
  ownerId: string;
  section: CdBuilderSection;
  pdfRedirectUrl: string;
  orderDetails: IOrderDetailsData;
}

export const CdBuilder: React.FC<ICdBuilderProps> = ({ orderDetails, ownerId, section, pdfRedirectUrl }) => {
  const [orderState] = useApplicationContext();
  const topCdNavigationNodeRef = useRef(null);
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(orderNavigationSlice.actions.setup({ tab: 'Fees' }));
  }, [dispatch, orderDetails.transaction.file_number]);
  const history = useHistory();
  const {
    cdItems,
    validation,
    cdItemsError,
    cdItemsFetchStatus,
    updateCdItemsError,
    updateCdItemsFetchStatus,
    refiCondition,
  } = useSelector(({ order }: RootStore) => order.cdBuilder);

  const items = cdItems[ownerId].items;
  const [initialized, setInitialized] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [onSaveSuccess, setOnSaveSuccess] = useState<INavbarLink | null>(null);
  const [showRefiDetailsModal, setShowRefiDetailsModal] = useState(false);
  const [showRefiEligibilityModal, setShowRefiEligibilityModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const saved = cdItems[ownerId] ? cdItems[ownerId].changesSaved : false;

  const pageInProgress = inProgress || updateCdItemsFetchStatus.fetching;
  const pageError = error || cdItemsError || updateCdItemsError;

  const validationResult = validation[ownerId];
  const showValidationResult = validationResult ? !validationResult.valid : false;
  // do not use an order details from the cdItem microservice. Use a single order details store
  const ownerDetails: IOwnerDetailsServiceItem = useMemo(() => getOwnerDetails(orderDetails), [orderDetails]);

  // constructor and componentDidUpdate replaced by useEffect
  // loadItemsData and swapData moved to dispatch

  const prevSection = usePrevious(section);

  useEffect(() => {
    if (!initialized || prevSection !== section) {
      setInitialized(true);
      dispatch(loadItemsData(ownerId, orderDetails.parties, ownerDetails));
      dispatch(
        setRefiDiscountByState(
          ownerDetails.addressInfo.state,
          orderDetails.property.countyFips || '',
          orderDetails.client.name || ''
        )
      );
    }
  }, [ownerId, orderDetails, ownerDetails, dispatch, initialized, prevSection, section]);

  useEffect(() => {
    // moved from validateAndSave function
    if (validationResult && !validationResult.valid && topCdNavigationNodeRef && topCdNavigationNodeRef.current) {
      window.scrollTo({
        behavior: 'smooth',
        top: (topCdNavigationNodeRef.current as any).offsetTop,
      });
    }
  }, [validationResult, topCdNavigationNodeRef]);

  useEffect(() => {
    if (orderState.isEditingSingleField) {
      dispatch(validateAndSave(ownerId, orderDetails));
    }
  }, [orderState.isEditingSingleField, dispatch, orderDetails, ownerId]);

  const showSeller = ownerDetails.transactionType === 'purchase';

  const transformToNavbarLinkWithSaveDialog = (item: INavigationItemProps): INavigationItemProps => ({
    ...item,
    onClick: (href?: string) => {
      onLinkClick(href);
    },
  });

  const transformedSectionNavbarItems = cdBuilderNavigationOptions(ownerId).map(transformToNavbarLinkWithSaveDialog);
  const onChange = (item: ICDV3ViewItem) => dispatch(replaceCdItem(ownerId, item));

  const onMove = (item: ICDV3ViewItem) => {
    const newItem = updateMoveAttributesBasedOnType(item);
    if (newItem) {
      dispatch(replaceCdItem(ownerId, newItem));
      items.asArray.filter((t) => t.type === eCDV3ItemTypes.sales_tax && t.parent_id === newItem.id).forEach(onMove);
    }
  };

  const onRemove = (item: ICDV3ViewItem) => {
    dispatch(removeItem(ownerId, item));
  };

  const client = orderDetails.parties.find((p) => p.purpose === ePartyPurpose.client);
  const lender = orderDetails.parties.find((p) => p.purpose === ePartyPurpose.lender);
  const doShowAppraisalFee = FullName(client) === 'Unison' || FullName(lender) === 'Unison';
  const addRowActions = generateAddRowActions(
    ownerId,
    orderDetails.property.state,
    doShowAppraisalFee,
    dispatch,
    () => {}
  );
  const activeSectionNavbarItem =
    section === 'section-a-j' ? transformedSectionNavbarItems[0] : transformedSectionNavbarItems[1];
  const hasLenderPremium = !!items.asArray.find(
    (item: ICDV3ViewItem) => item.type === eCDV3ItemTypes.title_fee && item.sub_type === eTitleFees.lenders_policy
  );

  const onEligibilityCheck = () => setShowRefiEligibilityModal(true);

  const onEligibilityClose = () => setShowRefiEligibilityModal(false);

  const { action: doOrderUpdate } = useUpdateOrderTimberHook();

  const onEligibilitySubmit = async (refiDiscount: IRefiDiscount) => {
    setInProgress(true);
    await doOrderUpdate(orderDetails, {
      refiDiscount: {
        applied: refiDiscount.applied,
        finalizedAt: refiDiscount.finalized_at,
      },
    });
    await dispatch(loadItemsData(ownerId, orderDetails.parties, ownerDetails));
    setInProgress(false);
    setShowRefiEligibilityModal(false);
  };

  const onGenerate = () => {
    setInProgress(true);
    const promise = callValidateAndSave();
    if (promise) {
      promise.then(() => {
        generateClosingDisclosureAsync(ownerId)
          .then((result) => {
            const error = result instanceof Error ? result : null;
            if (error) {
              setError(error);
              setInProgress(false);
            } else {
              // if there were no errors, then we redirect
              setInProgress(false);
              window.location.href = pdfRedirectUrl.replace('{ORDER_ID}', ownerId);
            }
          })
          .catch((reason) => {
            setInProgress(false);
            setError(reason);
          });
      });
    } else {
      setInProgress(false);
    }
  };

  const needToSave = (): boolean => {
    const dirtySectionAJItems = items.asArray
      .filter((item: ICDV3ViewItem) => CDItemTypesBySection.aj.indexOf(item.type) >= 0)
      .filter((item: ICDV3ViewItem) => item.dirty);
    const deletedSectionAJItems = cdItems[ownerId].deletedItems
      .filter((item: ICDV3ViewItem) => CDItemTypesBySection.aj.indexOf(item.type) >= 0)
      .filter((item: ICDV3ViewItem) => !item.new);
    const sectionAJItemsNeedSave = dirtySectionAJItems.length > 0 || deletedSectionAJItems.length > 0;
    const dirtySectionKNItems = items.asArray
      .filter((item: ICDV3ViewItem) => CDItemTypesBySection.kn.indexOf(item.type) >= 0)
      .filter((item: ICDV3ViewItem) => item.dirty);
    const deletedSectionKNItems = cdItems[ownerId].deletedItems
      .filter((item: ICDV3ViewItem) => CDItemTypesBySection.kn.indexOf(item.type) >= 0)
      .filter((item: ICDV3ViewItem) => !item.new);
    const sectionKNItemsNeedSave = dirtySectionKNItems.length > 0 || deletedSectionKNItems.length > 0;

    return sectionAJItemsNeedSave || sectionKNItemsNeedSave;
  };

  const navigateTo = (href?: string | null) => {
    if (href && href[0] === '#') {
      window.location.hash = href;
    } else if (href && href.includes('spa')) {
      href && history.push(href);
    } else {
      if (!!href) window.location.href = href;
    }
  };

  const trySaveAndNavigateTo = (href?: string | null) => {
    setShowSaveModal(true);
    setOnSaveSuccess({ content: '', href: href || '' });
  };

  const onNavigateAway = NavigateAwayHelper(needToSave, trySaveAndNavigateTo);

  const onLinkClick = (href?: string | null) => {
    if (needToSave()) {
      trySaveAndNavigateTo(href);
    } else {
      navigateTo(href);
    }
  };

  const onRefiDetailsOpen = () => setShowRefiDetailsModal(true);

  const onRefiDetailsClose = () => setShowRefiDetailsModal(false);

  const onSave = async () => await callValidateAndSave();

  const hasEditPermissions: boolean = useHasPermissions([ePermissions.CdItemsV3Edit]);

  // validateAndSave moved to dispatch
  const callValidateAndSave = async () => await dispatch(validateAndSave(ownerId, orderDetails));

  const onYesClick = async () => {
    try {
      await callValidateAndSave();
      setShowSaveModal(false);
      if (onSaveSuccess) navigateTo(onSaveSuccess.href);
    } catch (e: any) {
      setShowSaveModal(false);
    }
  };

  const onNoClick = () => {
    setShowSaveModal(false);

    if (onSaveSuccess) {
      navigateTo(onSaveSuccess.href);
    }
  };

  return (
    <PageLoading isLoading={cdItemsFetchStatus.fetching}>
      <ErrorMessage error={pageError} />
      <BlockRedirection
        isDirty={needToSave()}
        saveChanges={callValidateAndSave}
        showSaveSpinner={cdItemsFetchStatus.fetching || updateCdItemsFetchStatus.fetching}
        showSaveSuccess={saved}
      />
      <ModalHandler trySaveCDItems={onSave} />
      <ReissueDiscountAlertContainer />
      {!!(ownerDetails.refiDiscount && showRefiEligibilityModal) && (
        <RefiEligibilityModal
          loading={pageInProgress}
          onClose={onEligibilityClose}
          onSubmit={onEligibilitySubmit}
          refiDiscount={ownerDetails.refiDiscount}
          state={ownerDetails.addressInfo.state}
          refiConditionForState={refiCondition}
        />
      )}
      {showSaveModal && (
        <SaveDialog
          onYesClick={onYesClick}
          onNoClick={onNoClick}
          showSaveSpinner={cdItemsFetchStatus.fetching || inProgress}
          showSaveSuccess={saved}
        />
      )}
      {!!(ownerDetails.refiDiscount && showRefiDetailsModal) && (
        <RefiDetailsModal
          onClose={onRefiDetailsClose}
          refiDiscount={ownerDetails.refiDiscount}
          refiConditionForState={refiCondition}
        />
      )}
      <div className="cd-title" ref={topCdNavigationNodeRef}>
        <CdNavigation
          active={activeSectionNavbarItem}
          linkItems={transformedSectionNavbarItems}
          loading={pageInProgress}
          onGenerate={onGenerate}
          onSave={onSave}
          hasEditPermission={hasEditPermissions}
          ownerId={ownerId}
          saved={saved}
          showSectionNav={ownerDetails.transactionType === 'purchase'}
        />
      </div>
      {!!(showValidationResult && validationResult && !validationResult.valid && validationResult.items) && (
        <ValidationSummary summary="We did not save. There were some invalid items." items={validationResult.items} />
      )}
      {section === 'section-a-j' && (
        <>
          {orderStatusIsNotLateStageOrArchived(ownerDetails.orderStatus) &&
          ownerDetails.refiDiscount &&
          ownerDetails.refiDiscount.eligibility === 'ConditionallyEligibleByState' &&
          !ownerDetails.refiDiscount.finalized_at &&
          hasLenderPremium ? (
            <NotificationLenderPremiumConditionalRate onCheckNowClick={onEligibilityCheck} />
          ) : null}
          <LoanDetails
            {...ownerDetails}
            orderId={ownerId}
            onNavigateAway={onNavigateAway}
            hasEditPermissions={hasEditPermissions}
          />
          <LoanCosts
            originationActions={addRowActions.origination}
            originationContent={
              items.origination
                ? generateSectionItems(items.origination.items, {
                    onChange,
                    onRemove,
                    showSeller,
                    hasEditPermissions,
                  })
                : null
            }
            originationTotals={items.origination ? items.origination.totals : null}
            notShoppedForActions={addRowActions.notShoppedFor}
            notShoppedForContent={
              items.services_not_shopped_for
                ? generateServicesSectionItems(items.services_not_shopped_for.items, {
                    onChange,
                    onDetailsClick: onRefiDetailsOpen,
                    onEligibilityCheck: onEligibilityCheck,
                    onMove,
                    onRemove,
                    showSeller,
                    hasEditPermissions,
                  })
                : null
            }
            notShoppedForTotals={items.services_not_shopped_for ? items.services_not_shopped_for.totals : null}
            shoppedForActions={addRowActions.shoppedFor}
            shoppedForContent={
              items.services_shopped_for
                ? generateServicesSectionItems(items.services_shopped_for.items, {
                    onChange,
                    onDetailsClick: onRefiDetailsOpen,
                    onEligibilityCheck: onEligibilityCheck,
                    onMove,
                    onRemove,
                    showSeller,
                    hasEditPermissions,
                  })
                : null
            }
            shoppedForTotals={items.services_shopped_for ? items.services_shopped_for.totals : null}
            showSeller={showSeller}
            totalLoanCosts={items.total_loan_costs}
            hasEditPermissions={hasEditPermissions}
          />
          <OtherCosts
            initialEscrowActions={addRowActions.initialEscrow}
            initialEscrowContent={
              items.escrow_at_closing
                ? generateSectionItems(items.escrow_at_closing.items, {
                    onChange,
                    onRemove,
                    showSeller,
                    hasEditPermissions,
                  })
                : null
            }
            initialEscrowTotals={items.escrow_at_closing ? items.escrow_at_closing.totals : null}
            otherActions={addRowActions.other}
            otherContent={
              items.other_costs
                ? generateSectionItems(items.other_costs.items, {
                    onChange,
                    onRemove,
                    showSeller,
                    hasEditPermissions,
                  })
                : null
            }
            otherTotals={items.other_costs ? items.other_costs.totals : null}
            prepaidsActions={addRowActions.prepaids}
            prepaidsContent={
              items.prepaids
                ? generateSectionItems(items.prepaids.items, {
                    onChange,
                    onRemove,
                    showSeller,
                    hasEditPermissions,
                  })
                : null
            }
            prepaidsTotals={items.prepaids ? items.prepaids.totals : null}
            showSeller={showSeller}
            taxesActions={addRowActions.taxes}
            taxesContent={
              items.recording_fees_and_taxes
                ? generateTaxesSectionItems(items.recording_fees_and_taxes.items, {
                    onChange,
                    onRemove,
                    showSeller,
                    hasEditPermissions,
                  })
                : null
            }
            taxesTotals={items.recording_fees_and_taxes ? items.recording_fees_and_taxes.totals : null}
            totalOtherCosts={items.total_other_costs}
            hasEditPermissions={hasEditPermissions}
          />
          <TotalClosingCosts
            showSeller={showSeller}
            totalClosingCosts={items.total_closing_costs}
            creditsContent={
              items.lender_credits
                ? generateSectionItems(items.lender_credits, {
                    onChange,
                    onRemove,
                    showSeller,
                    hasEditPermissions,
                  })
                : null
            }
            hasEditPermissions={hasEditPermissions}
          />
          <PayoffsAndPayments
            items={items.payoffs}
            showSeller={showSeller}
            payoffActions={addRowActions.payoffs}
            onChange={onChange}
            onRemove={onRemove}
            hasEditPermissions={hasEditPermissions}
          />
        </>
      )}
      {section !== 'section-a-j' && (
        <PurchaseCosts
          costs={items.total_closing_costs}
          totals={items.total_purchase_costs}
          dueFromBuyer={items.due_from_buyer}
          paidAlreadyByBuyer={items.paid_already_by_buyer}
          dueToSeller={items.due_to_seller}
          dueFromSeller={items.due_from_seller}
          onChange={onChange}
          onRemove={onRemove}
          ownerId={ownerId}
          purchasePrice={ownerDetails.purchasePrice || 0}
          dueFromBuyerActions={addRowActions.dueFromBuyer}
          paidAlreadyByBuyerActions={addRowActions.paidAlreadyByBuyer}
          sellerAtClosing={items.total_closing_costs.subtotals.seller_at_closing || 0}
          onNavigateAway={onNavigateAway}
          hasEditPermissions={hasEditPermissions}
        />
      )}

      <ReissueDiscountsContainer onSave={onSave} />
      <CdNavigation
        active={activeSectionNavbarItem}
        linkItems={transformedSectionNavbarItems}
        loading={pageInProgress}
        onGenerate={onGenerate}
        onSave={onSave}
        ownerId={ownerId}
        saved={saved}
        showSectionNav={ownerDetails.transactionType === 'purchase'}
        hasEditPermission={hasEditPermissions}
      />
    </PageLoading>
  );
};
